// import type { client, TStoreRouter } from "../../../trpc/trpc";

import { serializeDate } from "@tillix/utils";
import type { MapProp } from "@tillix/utils/types";
import {
  computed,
  type IObservableArray,
  observable,
  reaction,
  toJS,
} from "mobx";
import {
  _async,
  _await,
  applySnapshot,
  asMap,
  customRef,
  detach,
  fromSnapshot,
  getRootStore,
  isoStringToDateTransform,
  Model,
  type ModelClass,
  type ModelCreationData,
  modelAction,
  modelFlow,
  modelSnapshotInWithMetadata,
  prop,
  type Ref,
  type SnapshotInOf,
} from "mobx-keystone";
import { getStoreConfig } from "../../../config";
import { getEnv } from "../../../env";
import {
  type CoreError,
  RefError,
  SyncError,
  UninitializedError,
} from "../error";
import { addToRefArray, inferModelRef, removeFromRefArray } from "./ref";
import { buildQueryWindow, defaultSortByAction } from "./utils";
import type { ResourceApi } from "../../../orpc/client";
import type { IsStore } from "../../../utils/types";
import type TilliXStore from "../../core/tillix";
import type RootStore from "../../root";
import type { TModel } from "./t-model";

export interface DefaultQueryOptions<Q extends Record<string, any>> {
  limit?: number;
  cursor?: string;
  query?: Q;
  sort?: {};
  page?: number;
  forceRefresh?: boolean;
}

export interface QueryHistory<Q extends Record<string, any>>
  extends Omit<DefaultQueryOptions<Q>, "query" | "sort"> {
  query: string; // JSON stringified query
  sort: string; // JSON stringified sort
  ids: { id: string; date: Date }[];
  window: [number, number];
  cursorDelta: number;
  createdAt: number;
}

export type DataStoreStrategy<T extends TModel> =
  | {
      type: "all";
    }
  | {
      type: "lru";
      algorithm: (model: T) => boolean;
      maxSize: number;
    }
  | {
      type: "none";
    };

type WhereInput = {
  AND?: any;
  OR?: any;
  NOT?: any;
  [key: string]: unknown;
};

// all stores that hold unbound/external data inherit from this class
/**
 * Upper bound on pages one hydrate will follow (100 × 100 rows = 10,000). A
 * guard against a server that never says `hasMore: false`, not a product limit.
 */
const MAX_HYDRATE_PAGES = 100;

export abstract class TStore<
    R extends TModel,
    Q extends WhereInput,
    S extends ResourceApi<
      // sickos.jpeg
      MapProp<
        string,
        Date,
        MapProp<undefined, null, SnapshotInOf<R>>,
        `${string}At`
      >
    >,
  >
  extends Model(<_R extends TModel, _Q extends WhereInput, _S>() => ({
    _data: prop<_R[]>(() => observable.array<_R>([])),
    _selected: prop<Ref<_R>[]>(() => []),
    _hidden: prop<Ref<_R>[]>(() => []),
    loading: prop<boolean>(false).withSetter(),
    syncing: prop<boolean>(false).withSetter(),
    lastSync: prop<string>().withTransform(isoStringToDateTransform()),
  }))<R, Q, S>
  implements IsStore
{
  abstract Model: ModelClass<R>;
  abstract api: S;

  @observable
  protected queryHistory: QueryHistory<Q>[] = [];
  isStore: true = true;
  maxTimeBetweenEntries = 0;

  /**
   * When true, a `hydrate()` without a `cursor` loads every page (following
   * `nextCursor` until `hasMore` is false) instead of one. For stores whose
   * screens need the whole list — totals, filters and search run over the
   * store in memory. Pages are the API's default size unless a limit is passed.
   */
  loadAllPages = false;

  /**
   * Loads in flight, keyed by their parameters: a second identical call while
   * one runs gets the same promise, so two components hydrating the same
   * store at mount make one request sequence, not two.
   */
  private _inFlightHydrates = new Map<
    string,
    ReturnType<typeof this._hydrateFlow>
  >();

  errors: Error[] = [];

  modelRef = customRef<R>(inferModelRef(this.$modelType), {
    resolve: (ref) => {
      return this._data.find((model) => model.id === ref.id);
    },
    getId: (model) => {
      return (model as R).id;
    },
    onResolvedValueChange(ref, newModel, oldModel) {
      if (oldModel && !newModel) {
        console.debug("[%s]:detach %s", oldModel.$modelType, oldModel.id);
        detach(ref);
      }
    },
  });
  creationError = false;

  @observable
  initialized = false;

  get root(): RootStore {
    const rootStore = getRootStore<RootStore>(this);
    if (!rootStore) {
      throw new UninitializedError("RootStore");
    }
    return rootStore;
  }

  get tilliX(): TilliXStore {
    return this.root.tilliX;
  }

  // #region: 𐑼 Array Properties

  @observable
  activeFilters: string[] = [];

  @computed
  get data(): R[] {
    return this._data.filter(
      (d) => !this._hidden.includes(d.id as unknown as Ref<R>),
    );
  }

  @computed
  get selected(): R[] {
    return this._selected.map((s) => s.current);
  }

  @computed
  get hidden(): R[] {
    return this._hidden.map((h) => h.current);
  }

  @computed
  get map() {
    return asMap(observable.array(this.data.map((d) => [d.$modelId, d])));
  }

  @computed
  private get _map() {
    return asMap(observable.array(this._data.map((d) => [d.$modelId, d])));
  }

  // #region: 𐑼 Array Actions

  /**
   *
   * @param page
   * @param chunkSize
   * @returns
   */
  page(page = 0, chunkSize = 50) {
    const start = page * chunkSize;
    const end = start + chunkSize;
    if (start > this.data.length) return [];
    return this.data.slice(start, end);
  }

  /**
   *
   * @param data
   * @returns
   */
  @modelAction
  setData(data: R[]) {
    return (this._data as IObservableArray<R>).replace(data);
  }

  /**
   *
   * @param data
   * @returns
   */
  @modelAction
  addData(data: R[]) {
    return this._data.push(...data);
  }

  /**
   *
   * @param data
   * @returns
   */
  @modelAction
  setRaw(data: SnapshotInOf<R>[]) {
    return (this._data as IObservableArray<R>).replace(
      data.map((datum) => fromSnapshot(this.Model, datum)),
    );
  }

  /**
   * Seeds the store from server-rendered data (the hydrate hooks call this on
   * every page load / navigation).
   *
   * A store that loads every page (`loadAllPages`) and is already loaded
   * MERGES the seed — updates rows it has, adds new ones, drops nothing — so
   * a first-page seed can't shrink the full list every page shares. Anything
   * else REPLACES, exactly as `setRaw`.
   */
  @modelAction
  seedRaw(data: SnapshotInOf<R>[]) {
    if (!(this.loadAllPages && this.initialized)) {
      this.setRaw(data);
      return;
    }
    const incoming = new Map(data.map((item) => [item.id, item]));
    // Seed snapshots carry ISO-string dates, so compare as timestamps
    // (splitUpdatedAndStale compares a Date with whatever it's given).
    const updated = this._data.filter((model) => {
      const fresh = incoming.get(model.id) as
        | { updatedAt?: unknown }
        | undefined;
      if (!fresh?.updatedAt) return false;
      const freshAt = new Date(fresh.updatedAt as string | Date).getTime();
      return model.updatedAt.valueOf() <= freshAt;
    });
    this.applyUpdates(updated, incoming as Map<string, SnapshotInOf<R>>);
    this.addNewData(this.findNewData(data as unknown as R[]));
  }

  /**
   *
   * @param data
   * @returns
   */
  @modelAction
  addRaw(data: SnapshotInOf<R>[]) {
    return this._data.push(
      ...data.map((datum) => fromSnapshot(this.Model, datum)),
    );
  }

  /**
   *
   * @param datum
   * @returns
   */
  @modelAction
  removeData(datum: R) {
    // const t0 = performance.now();

    // we set this flag to avoid weird recompute errors on the underlying observable when we attempt to detach it/ remove from the backing store
    datum.detaching = true;
    const index = this._data.findIndex((d) => d.$modelId === datum.$modelId);
    if (~datum.selectionIndex) {
      this._selected.splice(datum.selectionIndex, 1);
    }
    // const t1 = performance.now();

    if (~datum.hiddenIndex) {
      this._hidden.splice(datum.hiddenIndex, 1);
    }
    // const t2 = performance.now();

    const spliceSuccess = (this._data as IObservableArray<R>).splice(index, 1);
    // const t3 = performance.now();

    detach(datum);

    void getStoreConfig().storageAdapter.delete(
      datum.$modelType,
      datum.$modelId,
    );

    return spliceSuccess;
  }

  /**
   *
   * @returns
   */
  @modelAction
  clearData() {
    return (this._data as IObservableArray<R>).clear();
  }

  /**
   *
   * @param item
   * @returns
   */
  @modelAction
  public hide(item: R["id"] | R["$modelId"] | R) {
    return addToRefArray(this as unknown as AnyStore, "_hidden", item);
  }

  /**
   *
   * @param item
   * @returns
   */
  @modelAction
  public unhide(item: R["id"] | R["$modelId"] | R) {
    return removeFromRefArray(this as unknown as AnyStore, "_hidden", item);
  }

  /**
   *
   * @param item
   * @returns
   */
  @modelAction
  public select(item: R["id"] | R["$modelId"] | R) {
    return addToRefArray(this as unknown as AnyStore, "_selected", item);
  }

  /**
   *
   * @param item
   * @returns
   */
  @modelAction
  public deselect(item: R["id"] | R["$modelId"] | R) {
    return removeFromRefArray(this as unknown as AnyStore, "_selected", item);
  }

  @modelAction
  protected defaultSortBy(attribute: keyof R & string) {
    defaultSortByAction(this._data as IObservableArray<R>, attribute);
  }

  // #region: 𐑼 Array Methods

  public byId(
    id: R["id"] | R["$modelId"],
    includeHidden?: boolean,
  ): R | undefined {
    return includeHidden ? this._map.get(id) : this.map.get(id);
  }

  public filter(
    filterFunc: (resource: R, index: number, array: R[]) => boolean,
    includeHidden?: boolean,
  ): R[] {
    const data = includeHidden ? this._data : this.data;
    return data.filter(filterFunc);
  }

  public async asyncFilter(
    filterFunc: (resource: R, index: number, array: R[]) => boolean,
    includeHidden?: boolean,
  ): Promise<R[]> {
    const data = includeHidden ? this._data : this.data;
    return data.TX_CORE__asyncFilter(filterFunc);
  }

  // #region: 🛜 Sync Methods

  @modelAction
  public setLastSync(date?: Date) {
    this.lastSync = date ?? new Date();
  }

  async getAll({
    limit,
    cursor,
    query,
    sort,
    forceRefresh = false,
  }: DefaultQueryOptions<Q>): Promise<{
    data: SnapshotInOf<R>[];
    time: number;
    nextCursor: string | null;
    hasMore: boolean;
  }> {
    const response = await this.api.get({
      limit,
      cursor,
      where: query,
      orderBy: sort,
      forceRefresh,
    });

    console.debug(
      { response },
      `Raw response from API for ${this.$modelType} getAll`,
    );

    const paginated = response;

    if (!paginated?.items) {
      throw new SyncError(`[${this.$modelType} - getAll] - No data found.`);
    }

    const validData: SnapshotInOf<R>[] = [];

    for (const item of paginated.items as unknown as SnapshotInOf<R>[]) {
      try {
        const sn = modelSnapshotInWithMetadata(this.Model, item as any);
        const modelInstance = fromSnapshot<R>(
          serializeDate(sn) as SnapshotInOf<R>,
        );

        if (modelInstance.creationError) {
          continue;
        }
        detach(modelInstance);

        validData.push(item);
      } catch (error) {
        // if model creation fails, record the error and skip this item
        this.addError({
          message: `Invalid ${this.$modelType} snapshot, error: ${error instanceof Error ? error.message : "Unknown error"}`,
        } as Error);
        if (getEnv().env === "development") {
          console.error(
            `Failed to create model instance for ${this.$modelType} with id ${item.id}:`,
            error,
          );
        }
      }
    }

    return {
      data: validData,
      time: Date.now(),
      nextCursor: paginated.nextCursor,
      hasMore: paginated.hasMore,
    };
  }

  // Step 1 fetches fresh data.
  // Step 2–3 updates local data.
  // Step 6 cleans up stale data.
  @modelFlow
  private _hydrateFlow = _async(function* hydrate(
    this: typeof TStore.prototype,
    params: Partial<DefaultQueryOptions<Q>> = {},
    finalSort: (keyof R & string) | undefined,
    shouldLoadAllPages: boolean,
  ) {
    const { limit, sort, query, cursor, forceRefresh } = params ?? {};

    if (
      this.maxTimeBetweenEntries > 0 &&
      this.isQueryCovered({ limit, cursor, sort, query })
    )
      return;
    this.setSyncing(true);

    // 1. Fetch fresh entries
    let { data, nextCursor, hasMore } = yield* _await(
      this.getAll({ limit, sort, query, cursor, forceRefresh }),
    );

    // 1b. A store that must hold its whole list (`loadAllPages`), or an
    //     explicit `hydrateAll()`, follows the cursor when the caller didn't
    //     ask for a specific page. Every page keeps the caller's
    //     `forceRefresh`: the tenant re-syncs one page per request, so a
    //     forced refresh pages exactly like a plain read. Everything is merged
    //     once below.
    if (shouldLoadAllPages && cursor === undefined) {
      const all = [...data];
      const seenCursors = new Set<string>();
      let pages = 1;
      while (hasMore && nextCursor) {
        if (seenCursors.has(nextCursor)) {
          // The server repeated a cursor: stop rather than loop forever.
          nextCursor = null;
          hasMore = false;
          break;
        }
        if (pages >= MAX_HYDRATE_PAGES) {
          console.warn(
            `[${this.$modelType}] stopped loading after ${MAX_HYDRATE_PAGES} pages; the list is truncated`,
          );
          break;
        }
        seenCursors.add(nextCursor);
        const next = yield* _await(
          this.getAll({
            limit,
            sort,
            query,
            cursor: nextCursor,
            forceRefresh,
          }),
        );
        all.push(...next.data);
        ({ nextCursor, hasMore } = next);
        pages += 1;
      }
      data = all;
    }

    // 2. Compare incoming vs existing
    const incoming = new Map(data.map((i) => [i.id, i]));
    const [updated, _stale] = this.splitUpdatedAndStale(incoming);

    // 3. Apply updates, add new entries, then sort
    this.applyUpdates(updated, incoming);
    const newData = this.findNewData(data);
    this.addNewData(newData);
    if (finalSort) {
      this.defaultSortBy(finalSort);
    }
    // this.defaultSortBy("updatedAt");

    // 4. Drop local rows whose ids are not in `data` (e.g. deleted on the
    //    server). Only when limit, cursor, and query are all unset: then
    //    `data` is the full list. A query-scoped hydrate narrows the result
    //    set by definition, so rows outside that query's scope must not be
    //    treated as stale.
    // if (limit === undefined && cursor === undefined && query === undefined) {
    //   const incomingIds = new Set(
    //     data.map((i) => i.id).filter((id): id is string => Boolean(id)),
    //   );
    //   const toRemove = [...this._data].filter((d) => !incomingIds.has(d.id));
    //   for (const model of toRemove) {
    //     this.removeData(model);
    //   }
    // }

    // 5. Update query history + figure out stale candidates

    // FIXME: re-enable this once it doesnt always throw
    // const staleIdsToCheck = this.updateQueryHistory(
    //   data,
    //   { query, limit, cursor, sort },
    //   stale,
    // );

    // // 6. Confirm stale with backend
    // if (staleIdsToCheck && staleIdsToCheck.length > 0) {
    //   yield* _await(this.checkAndRemoveStaleData(staleIdsToCheck));
    // }

    this.setSyncing(false);
    this.setLastSync();

    return { fromQuery: data, data: this.data, nextCursor, hasMore };
  });

  /**
   * Fetches into the store. A `loadAllPages` store follows `hasMore` through
   * every page when no `cursor` is given; otherwise one page.
   */
  hydrate(
    params: Partial<DefaultQueryOptions<Q>> = {},
    finalSort?: keyof R & string,
  ) {
    return this._sharedHydrate(params, finalSort, this.loadAllPages);
  }

  /**
   * Loads every page of the query into the store, whatever `loadAllPages`
   * says. For one-off "give me everything" needs on a store that normally
   * pages (e.g. an export).
   */
  hydrateAll(params: Omit<Partial<DefaultQueryOptions<Q>>, "cursor"> = {}) {
    return this._sharedHydrate(params, undefined, true);
  }

  private _sharedHydrate(
    params: Partial<DefaultQueryOptions<Q>>,
    finalSort: (keyof R & string) | undefined,
    shouldLoadAllPages: boolean,
  ) {
    const key = JSON.stringify([params, finalSort, shouldLoadAllPages]);
    const existing = this._inFlightHydrates.get(key);
    if (existing) return existing;

    // A plain whole-list load rides along with a forced one already running:
    // same rows, fresher. Never the reverse — a forced read has to reach the
    // tenant, so it never settles for a plain read's answer. Without this, a
    // screen that both fills the list and refreshes on visit walks every page
    // twice whenever the fill wins the race.
    if (shouldLoadAllPages && !params.forceRefresh && !params.cursor) {
      const forced = this._inFlightHydrates.get(
        JSON.stringify([
          { ...params, forceRefresh: true },
          finalSort,
          shouldLoadAllPages,
        ]),
      );
      if (forced) return forced;
    }

    const load = this._hydrateFlow(params, finalSort, shouldLoadAllPages);
    this._inFlightHydrates.set(key, load);
    const forget = () => {
      if (this._inFlightHydrates.get(key) === load) {
        this._inFlightHydrates.delete(key);
      }
    };
    load.then(forget, forget);
    return load;
  }

  // #region: 🔄 Lifecycle Methods
  @computed
  get ready(): boolean | CoreError[] {
    if (this.creationError) {
      return this.errors;
    }

    return this.initialized;
  }

  protected onAttachedToRootStore(_root: RootStore): void | (() => void) {
    if (getEnv().persistLocally) {
      const callbackIds: Record<string, number | null> = {
        hidden: null,
        selected: null,
        lastSync: null,
        data: null,
      };

      const hiddenItemDisposer = reaction(
        (_rx) => {
          if (getEnv().trace) {
            _rx.trace(true);
          }
          return this._hidden.map((h) => h.id);
        },
        (ids) => {
          void getStoreConfig()
            .storageAdapter.set(this.$modelType, "hidden-ids", ids)
            .finally(() => {
              callbackIds.hidden = null;
            });
        },
        {
          fireImmediately: true,
          name: `[STORE]-${this.$modelType}-hidden-disposer`,
          scheduler: (run) => {
            if (callbackIds.hidden) {
              cancelIdleCallback(callbackIds.hidden);
            }
            callbackIds.hidden = requestIdleCallback(run);
          },
        },
      );

      const selectedItemDisposer = reaction(
        (_rx) => {
          if (getEnv().trace) {
            _rx.trace(true);
          }
          return this._selected.map((s) => s.id);
        },
        (ids) => {
          void getStoreConfig()
            .storageAdapter.set(this.$modelType, "selected-ids", ids)
            .finally(() => {
              callbackIds.selected = null;
            });
        },
        {
          fireImmediately: true,
          name: `[STORE]-${this.$modelType}-selected-disposer`,
          scheduler: (run) => {
            if (callbackIds.selected) {
              cancelIdleCallback(callbackIds.selected);
            }
            callbackIds.selected = requestIdleCallback(run);
          },
        },
      );

      const lastSyncDisposer = reaction(
        () => this.lastSync,
        (sync) => {
          void getStoreConfig()
            .storageAdapter.set(this.$modelType, "last-sync", sync.valueOf())
            .finally(() => {
              callbackIds.lastSync = null;
            });
        },
        {
          fireImmediately: true,
          name: `[STORE]-${this.$modelType}-synctime-disposer`,
          scheduler: (run) => {
            if (callbackIds.lastSync) {
              cancelIdleCallback(callbackIds.lastSync);
            }
            callbackIds.lastSync = requestIdleCallback(run);
          },
        },
      );

      return () => {
        Object.entries(callbackIds).forEach(([key, id]) => {
          if (typeof id === "number") cancelIdleCallback(id);
          callbackIds[key] = null;
        });
        hiddenItemDisposer();
        selectedItemDisposer();
        lastSyncDisposer();
      };
    }
  }

  protected onInit(): void {
    if (getEnv().env === "development" && typeof window !== "undefined") {
      console.debug("Initializing internal store:", this.$modelType);
    }
  }

  private splitUpdatedAndStale(incoming: Map<string, R>): [R[], R[]] {
    return this._data.reduce(
      (arr, current) => {
        const _incoming = incoming.get(current.id);
        const isIncomingNewer =
          _incoming &&
          current.updatedAt.valueOf() <= _incoming.updatedAt.valueOf();

        if (isIncomingNewer) arr[0].push(current);
        else arr[1].push(current);

        return arr;
      },
      [[], []] as [R[], R[]],
    );
  }

  protected applyUpdates(
    existing: R[],
    incoming: Map<string, SnapshotInOf<R>>,
  ) {
    existing.forEach((model) => {
      const fresh = incoming.get(model.id);
      if (fresh) {
        applySnapshot(model, serializeDate(toJS(fresh)) as SnapshotInOf<R>);
      }
    });
  }

  private findNewData(data: R[]): R[] {
    return data.filter((i) => !this._data.find((d) => d.id === i.id));
  }

  @modelAction
  private addNewData(newData: R[]) {
    newData.forEach((data) => {
      const isSnapshot = data.$modelType && data.$;
      if (isSnapshot) {
        this._data.push(data);
      } else {
        this._data.push(
          fromSnapshot(this.Model, serializeDate(data) as SnapshotInOf<R>),
        );
      }
    });
  }

  // TODO: use the query comparison logic here + hash with sorted keys to check for query equality
  private isQueryCovered(newQuery: DefaultQueryOptions<Q>) {
    const haventMadeAnyQueriesYet = this.maxTimeBetweenEntries <= 0;
    if (haventMadeAnyQueriesYet) return false;

    const newWindow = buildQueryWindow(
      newQuery.cursor,
      newQuery.limit,
      this.maxTimeBetweenEntries,
    );

    for (const past of this.queryHistory) {
      const sameQuery =
        JSON.stringify(JSON.parse(past.query ?? "{}")) ===
        JSON.stringify(newQuery.query ?? {});
      const sameSort =
        JSON.stringify(JSON.parse(past.sort ?? "{}")) ===
        JSON.stringify(newQuery.sort ?? {});
      const sameLimit = past.limit === newQuery.limit;
      const sameCursor = past.cursor === newQuery.cursor;

      const exactDuplicateQuery =
        sameQuery && sameSort && sameLimit && sameCursor;

      if (exactDuplicateQuery) {
        return true;
      }

      // for queries with same parameters but diff cursor, check if the past window covers the new window
      if (sameQuery && sameSort && sameLimit) {
        const pastWindow = buildQueryWindow(
          past.cursor,
          past.limit,
          this.maxTimeBetweenEntries,
        );

        const covers =
          pastWindow.start <= newWindow.start &&
          pastWindow.end >= newWindow.end;

        if (covers) return true;
      }
    }

    return false;
  }

  // #region: ❌ Error Handling
  private addError(error: CoreError) {
    if (!this.errors) {
      this.errors = [];
    }
    this.errors.push(error);
  }

  @modelAction
  handleReferenceCreationErrors(ref: Ref<R>, func: string) {
    if (ref?.maybeCurrent === undefined || !ref.isValid) {
      this.addError(
        new RefError(
          `[${this.$modelType} - ${func}] - ref resolves to undefined or is invalid`,
          this.$modelType,
          true,
        ),
      );
      return true;
    } else if (ref.maybeCurrent && !this._map.has(ref.maybeCurrent?.id)) {
      this.addError(
        new RefError(
          `[${this.$modelType} - ${func}] - ref does not exist in this store`,
          this.$modelType,
          true,
        ),
      );
      return true;
    }

    return false;
  }

  get allErrors() {
    return [
      ...(this.errors?.slice() ?? []),
      ...this._data.flatMap((d) => d.errors ?? []),
    ];
  }
}

export type AnyStore = TStore<TModel, any, any>;

export type StoreDataKeys<T extends TModel> = keyof Pick<
  TStore<T, any, any>,
  "_data" | "_hidden" | "_selected" | "lastSync"
>;

export type StorageFetchOpts = Partial<{
  [K in StoreDataKeys<TModel>]: boolean;
}>;

export type ModelSnapshotData<M extends TModel> = Omit<
  ModelCreationData<TStore<M, any, any>>,
  "_data"
> & {
  _data: SnapshotInOf<M>[];
};
